import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Check, Search } from "lucide-react";
import { useAppData, useConfigGuard } from "../../contexts/AppContext";
import { ModalConfirm } from "../../modal/ModalConfirm";

interface IClientSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Troca de cliente (GEN-3497 r3).
 *
 * Saiu da trilha e passou a viver no menu de conta, junto com o resto do que é "meu".
 * O racional é o do Notion: a trilha diz onde você está DENTRO do produto; em qual conta
 * você está é assunto do avatar. E, na prática, quase todo usuário da Taxly pertence a um
 * cliente só — para eles o degrau do cliente era ruído em toda página.
 *
 * `selectClient` continua sendo o do AppShell (limpa o localStorage e remonta o contexto).
 */
export const ClientSwitchModal = ({ isOpen, onClose }: IClientSwitchModalProps) => {
  const { validation, currentClient, selectClient } = useAppData();
  // Trocar de cliente limpa o estado da sessão: se houver edição não salva, o trabalho
  // seria descartado em silêncio.
  const { hasChange, setHasChange } = useConfigGuard();
  const [term, setTerm] = useState("");
  const [pendingClient, setPendingClient] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  // GEN-4080: a busca abre VAZIA a cada abertura. O modal fica montado no menu da conta, então o termo
  // da troca anterior sobrevivia — a pessoa reabria e via a lista filtrada por um nome que não digitou
  // agora ("Nenhum cliente com esse nome" sem ter buscado nada).
  useEffect(() => {
    // Limpa ao FECHAR (não ao abrir): assim a reabertura já nasce vazia, sem um quadro com o filtro velho.
    if (!isOpen) setTerm("");
  }, [isOpen]);

  const clients = useMemo(() => validation?.clients ?? [], [validation]);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) => client.name?.toLowerCase().includes(q));
  }, [clients, term]);

  const apply = (guidClient: string) => {
    if (hasChange) {
      setPendingClient(guidClient);
      return;
    }
    onClose();
    selectClient(guidClient);
  };

  const confirm = () => {
    if (!pendingClient) return;
    setHasChange(false);
    const guid = pendingClient;
    setPendingClient(null);
    onClose();
    selectClient(guid);
  };

  return (
    <>
      {/* `initialFocusRef`: o foco vai para a busca a cada abertura (o `autoFocus` só valia na primeira). */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered initialFocusRef={buscaRef}>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderLeft="12px solid #0263FF" borderRadius="8px">
          <ModalHeader fontSize="18px" fontFamily="Poppins" fontWeight="500">
            Trocar de cliente
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="20px">
            {/* A busca só aparece quando há lista o bastante para justificar. */}
            {clients.length > 6 ? (
              <Box pb="12px">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Search size={14} color="#77777B" />
                  </InputLeftElement>
                  <Input
                    ref={buscaRef}
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    placeholder="Buscar cliente"
                    fontSize="14px"
                    borderRadius="6px"
                    aria-label="Buscar cliente pelo nome"
                  />
                </InputGroup>
              </Box>
            ) : null}

            <Box maxH="340px" overflowY="auto" className="scrollbarsidebar">
              {filtered.length === 0 ? (
                <Text px="4px" py="10px" fontSize="14px" color="#77777B">
                  Nenhum cliente com esse nome.
                </Text>
              ) : (
                filtered.map((client) => {
                  const isCurrent = client.guid_client === currentClient?.guid_client;
                  return (
                    <Flex
                      key={client.guid_client}
                      as="button"
                      type="button"
                      w="100%"
                      align="center"
                      justify="space-between"
                      gap="8px"
                      px="10px"
                      py="10px"
                      borderRadius="6px"
                      textAlign="left"
                      fontSize="14px"
                      color="#303033"
                      backgroundColor={isCurrent ? "#F2F0F4" : "transparent"}
                      sx={{ "&:hover": { backgroundColor: "#F2F0F4" } }}
                      onClick={() => {
                        if (!isCurrent) apply(client.guid_client);
                      }}
                      aria-current={isCurrent ? "true" : undefined}
                    >
                      <Text noOfLines={1}>{client.name}</Text>
                      {isCurrent ? <Check size={16} color="#375CA9" /> : null}
                    </Flex>
                  );
                })
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ModalConfirm
        isOpen={Boolean(pendingClient)}
        setIsOpen={(open) => {
          if (!open) setPendingClient(null);
        }}
        title="Trocar de cliente"
        description="Você tem alterações que ainda não foram salvas. Trocar de cliente agora descarta essas alterações."
        buttonConfirmText="Trocar"
        buttonCancelText="Cancelar"
        callback={confirm}
        cancel={() => setPendingClient(null)}
        size="sm"
      />
    </>
  );
};
